import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Layout } from "@/components/Layout";
import { PlatformLayout } from "@/components/PlatformLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Parts from "@/pages/Parts";
import PartDetail from "@/pages/PartDetail";
import Machines from "@/pages/Machines";
import MachineDetail from "@/pages/MachineDetail";
import Movements from "@/pages/Movements";
import NfeEntry from "@/pages/NfeEntry";
import Purchases from "@/pages/Purchases";
import Imports from "@/pages/Imports";
import Inventory from "@/pages/Inventory";
import InventoryList from "@/pages/InventoryList";
import InventoryDetail from "@/pages/InventoryDetail";
import InventoryCount from "@/pages/InventoryCount";
import PartAudit from "@/pages/PartAudit";
import Suppliers from "@/pages/Suppliers";
import Locations from "@/pages/Locations";
import PlannedMachines from "@/pages/PlannedMachines";
import MaintenanceParts from "@/pages/MaintenanceParts";
import PartsUsedReport from "@/pages/PartsUsedReport";
import Collaborators from "@/pages/Collaborators";
import CurvaABC from "@/pages/CurvaABC";
import Alerts from "@/pages/Alerts";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import Companies from "@/pages/Companies";
import PlatformTeam from "@/pages/PlatformTeam";
import PlatformDashboard from "@/pages/PlatformDashboard";
import PlatformPlans from "@/pages/PlatformPlans";
import PlatformSubscriptions from "@/pages/PlatformSubscriptions";
import PlatformPayments from "@/pages/PlatformPayments";
import PlatformModules from "@/pages/PlatformModules";
import OperationalHome from "@/pages/operacional/OperationalHome";
import QRScan from "@/pages/operacional/QRScan";
import { Loader2 } from "lucide-react";

function PlatformProtected({ children, ownerOnly = false }) {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return (
      <div className="min-h-screen almox-bg flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_platform_admin) return <Navigate to="/" replace />;
  if (ownerOnly && !user.is_platform_owner) return <Navigate to="/plataforma/empresas" replace />;
  return children;
}

function ClientProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return (
      <div className="min-h-screen almox-bg flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.is_platform_admin) return <Navigate to="/plataforma" replace />;
  return children;
}


function ModuleProtected({ code, children }) {
  const { user, loading, hasModule } = useAuth();
  if (loading || user === null)
    return (
      <div className="min-h-screen almox-bg flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (!hasModule(code)) return <Navigate to="/" replace />;
  return children;
}

function PlatformHome() {
  const { user } = useAuth();
  return user?.is_platform_owner ? <PlatformDashboard /> : <Navigate to="/plataforma/empresas" replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/operacional" element={<ClientProtected><OperationalHome /></ClientProtected>} />
            <Route path="/operacional/scan" element={<ClientProtected><ModuleProtected code="estoque"><QRScan /></ModuleProtected></ClientProtected>} />
            <Route path="/plataforma" element={<PlatformProtected><PlatformLayout /></PlatformProtected>}>
              <Route index element={<PlatformHome />} />
              <Route path="empresas" element={<Companies />} />
              <Route path="planos" element={<PlatformPlans />} />
              <Route path="assinaturas" element={<PlatformSubscriptions />} />
              <Route path="pagamentos" element={<PlatformProtected><PlatformPayments /></PlatformProtected>} />
              <Route path="equipe" element={<PlatformProtected ownerOnly><PlatformTeam /></PlatformProtected>} />
              <Route path="modulos" element={<PlatformProtected><PlatformModules /></PlatformProtected>} />
            </Route>
            <Route element={<ClientProtected><Layout /></ClientProtected>}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/pecas" element={<ModuleProtected code="estoque"><Parts /></ModuleProtected>} />
              <Route path="/pecas/:id" element={<ModuleProtected code="estoque"><PartDetail /></ModuleProtected>} />
              <Route path="/maquinas" element={<ModuleProtected code="maquinas"><Machines /></ModuleProtected>} />
              <Route path="/maquinas/:id" element={<ModuleProtected code="maquinas"><MachineDetail /></ModuleProtected>} />
              <Route path="/movimentacoes" element={<ModuleProtected code="movimentacoes"><Movements /></ModuleProtected>} />
              <Route path="/entrada-nfe" element={<ModuleProtected code="movimentacoes"><NfeEntry /></ModuleProtected>} />
              <Route path="/compras" element={<ModuleProtected code="compras"><Purchases /></ModuleProtected>} />
              <Route path="/importacoes" element={<ModuleProtected code="importacoes"><Imports /></ModuleProtected>} />
              <Route path="/inventario" element={<ModuleProtected code="inventario"><InventoryList /></ModuleProtected>} />
              <Route path="/inventario/antigos" element={<ModuleProtected code="inventario"><Inventory /></ModuleProtected>} />
              <Route path="/inventario/:id" element={<ModuleProtected code="inventario"><InventoryDetail /></ModuleProtected>} />
              <Route path="/inventario/:id/contar" element={<ModuleProtected code="inventario"><InventoryCount /></ModuleProtected>} />
              <Route path="/auditoria-pecas" element={<ModuleProtected code="estoque"><PartAudit /></ModuleProtected>} />
              <Route path="/maquinas-planejadas" element={<ModuleProtected code="planejamento"><PlannedMachines /></ModuleProtected>} />
              <Route path="/manutencao-pecas" element={<ModuleProtected code="manutencao"><MaintenanceParts /></ModuleProtected>} />
              <Route path="/relatorios/pecas-usadas" element={<ModuleProtected code="manutencao"><PartsUsedReport /></ModuleProtected>} />
              <Route path="/curva-abc" element={<ModuleProtected code="curva_abc"><CurvaABC /></ModuleProtected>} />
              <Route path="/fornecedores" element={<ModuleProtected code="cadastros"><Suppliers /></ModuleProtected>} />
              <Route path="/localizacoes" element={<ModuleProtected code="cadastros"><Locations /></ModuleProtected>} />
              <Route path="/colaboradores" element={<ModuleProtected code="colaboradores"><Collaborators /></ModuleProtected>} />
              <Route path="/alertas" element={<ModuleProtected code="alertas"><Alerts /></ModuleProtected>} />
              <Route path="/usuarios" element={<Users />} />
              <Route path="/configuracoes" element={<Settings />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors theme="dark" />
      </AuthProvider>
    </div>
  );
}

export default App;
